/**
 * Trust wall carousel (sections/product-trust.liquid). The track is a native scroll-snap
 * scroller, so a swipe, a trackpad or a scroll wheel moves it with the platform's own momentum.
 * This element adds what the platform does not: prev and next buttons, arrow keys, Home and End
 * on the focused track, a "3 / 12" counter and a progress rule that follow the scroll, a label
 * per slide ("3 of 12"), and the "Read more" toggle on review cards whose text is cut. When every
 * slide fits without scrolling, the controls hide.
 *
 * Nothing moves by itself. With reduced motion the buttons and keys jump instead of gliding.
 */
import { defineElement, prefersReducedMotion } from '@wispaire/global';

const Base = globalThis.HTMLElement ?? class {};
const KEYS = { ArrowLeft: -1, ArrowRight: 1 };

/** Index of the offset nearest to `scrollLeft`: the slide that sits at the start of the track. */
export function nearestIndex(offsets, scrollLeft) {
  let best = 0;
  for (let i = 1; i < offsets.length; i++) {
    if (Math.abs(offsets[i] - scrollLeft) < Math.abs(offsets[best] - scrollLeft)) best = i;
  }
  return best;
}

export class TrustCarousel extends Base {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.position = this.querySelector('[data-position]');
    this.progress = this.querySelector('[data-progress]');
    this.controls = this.querySelector('[data-controls]');
    this.index = 0;

    this.slides.forEach((slide, i) => slide.setAttribute('aria-label', `${i + 1} of ${this.slides.length}`));
    this.prev?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    this.track.addEventListener('keydown', (event) => this.key(event));

    let queued = false;
    this.track.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          this.update();
        });
      },
      { passive: true }
    );

    // A wider or narrower window can make the strip start or stop overflowing.
    if (globalThis.ResizeObserver) new ResizeObserver(() => this.update()).observe(this.track);

    for (const button of this.querySelectorAll('[data-review-more]')) this.clamp(button);
    this.update();
  }

  /** Scrolls slide `index` (clamped) to the start of the track. */
  go(index) {
    const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
    slide?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  }

  key(event) {
    const last = this.slides.length - 1;
    const target = event.key in KEYS ? this.index + KEYS[event.key] : { Home: 0, End: last }[event.key];
    if (target === undefined) return;
    event.preventDefault();
    this.go(target);
  }

  /** Brings the counter, progress rule and buttons in line with the scroll position. */
  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const room = scrollWidth - clientWidth;
    if (this.controls) this.controls.hidden = room <= 1;
    const atEnd = scrollLeft >= room - 1;
    const first = this.slides[0]?.offsetLeft ?? 0;
    const last = this.slides.length - 1;
    this.index = atEnd ? last : nearestIndex(this.slides.map((slide) => slide.offsetLeft - first), scrollLeft);

    if (this.position) this.position.textContent = `${this.index + 1} / ${this.slides.length}`;
    this.progress?.style.setProperty('--progress', String(room > 0 ? Math.min(scrollLeft / room, 1) : 1));
    if (this.prev) this.prev.disabled = scrollLeft <= 1;
    if (this.next) this.next.disabled = atEnd;
  }

  /** Shows a review's "Read more" only when its clamped text is cut, and toggles it. */
  clamp(button) {
    const card = button.closest('.review');
    const body = card.querySelector('[data-review-body]');
    // Measure once the web fonts are in: text that fits in the fallback font can be cut in the real one.
    const fonts = globalThis.document?.fonts?.ready ?? Promise.resolve();
    fonts.then(() => (button.hidden = body.scrollHeight <= body.clientHeight + 1));
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      card.toggleAttribute('data-expanded', open);
    });
  }
}

defineElement('trust-carousel', TrustCarousel);
